/**
 * Recover a web tab that is still running a build a deploy has replaced.
 *
 * The web bundle loads some code on demand (the Stripe checkout, for one). A
 * deploy swaps every chunk file, so a tab opened before it asks for chunk
 * names that now 404 and checkout fails with "We could not open the secure
 * payment checkout" (founder QA, 2026-10-16). Reloading picks up the new build.
 *
 * A reload only happens when the server's index really names a different entry
 * bundle, so being offline or a flaky network never reloads the page, and at
 * most once a minute, so a genuinely broken deploy cannot loop.
 */

const STALE_CHUNK_MESSAGES = [
  /Loading module .* failed/i, // Expo/Metro web async require (AsyncRequireError)
  /Failed to fetch dynamically imported module/i, // Chrome
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /Loading chunk [\w-]+ failed/i, // webpack
];

const ENTRY_BUNDLE = /_expo\/static\/js\/web\/(entry-[a-f0-9]+\.js)/;
const RELOAD_KEY = 'mcai_stale_build_reload_at';
const RELOAD_COOLDOWN_MS = 60_000;

export function isStaleChunkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'AsyncRequireError' || name === 'ChunkLoadError') return true;
  return typeof message === 'string' && STALE_CHUNK_MESSAGES.some((pattern) => pattern.test(message));
}

/** The entry bundle file an HTML page or script URL names, e.g. `entry-97d2….js`. */
export function entryBundleName(text: string | null | undefined): string | null {
  const match = text ? ENTRY_BUNDLE.exec(text) : null;
  return match ? match[1] : null;
}

/** True when the reload cooldown allows a reload now; records the attempt. */
export function claimReload(storage: Pick<Storage, 'getItem' | 'setItem'>, now: number): boolean {
  const last = Number(storage.getItem(RELOAD_KEY) || 0);
  if (Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) return false;
  storage.setItem(RELOAD_KEY, String(now));
  return true;
}

function runningEntryBundle(): string | null {
  if (typeof document === 'undefined') return null;
  for (const script of Array.from(document.querySelectorAll('script[src]'))) {
    const name = entryBundleName((script as HTMLScriptElement).src);
    if (name) return name;
  }
  return null;
}

/**
 * Reloads the page when the server now serves a different build than this tab
 * runs. Resolves true when a reload was started (the caller should stop and
 * show nothing), false otherwise (handle the error as usual).
 */
export async function reloadIfNewerBuild(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return false;
  const running = runningEntryBundle();
  if (!running) return false;
  try {
    const response = await fetch(`${window.location.origin}/`, { cache: 'no-store' });
    if (!response.ok) return false;
    const served = entryBundleName(await response.text());
    if (!served || served === running) return false;
    if (!claimReload(window.sessionStorage, Date.now())) return false;
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/** Web only: any unhandled stale-chunk failure (e.g. a lazily loaded screen) reloads onto the new build. */
export function installStaleBuildRecovery(): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return () => {};
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isStaleChunkError(event.reason)) void reloadIfNewerBuild();
  };
  window.addEventListener('unhandledrejection', onRejection);
  return () => window.removeEventListener('unhandledrejection', onRejection);
}
